import { useGetApiV1IndividualpaymentSharedParcelEnums } from '@/shared/api/generated';
import { type LookupOption, mapDropdownOptions } from '@/shared/ui/molecules/LookupAutocomplete';

/**
 * Janela de frescor dos enums do fluxo de parcela.
 *
 * São listas de enum do backend — mudam com deploy, não com operação. O
 * `queryClient` do app usa `staleTime: 0`, o que faria cada abertura de formulário
 * repetir a mesma requisição; cinco minutos cobrem uma sessão de trabalho sem
 * prender o operador a um cadastro que mudou.
 */
const PARCEL_ENUMS_STALE_TIME = 300_000;

/** Listas de enum do fluxo de parcela, já normalizadas para os selects. */
export interface ParcelEnums {
  /** `ETypePayment` — alimenta o campo `typeId`. */
  readonly types: readonly LookupOption[];
  /** `EStatusPayment` — inclui `WaitingIntegration = 0`. */
  readonly statuses: readonly LookupOption[];
  /** `EPaymentMethod` completo. Para o dropdown restrito ao plano use `useAvailablePaymentMethods`. */
  readonly paymentMethods: readonly LookupOption[];
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * Enums do fluxo de parcela — tipo, status e forma de pagamento.
 *
 * Uma requisição só (`GET /individualpayment/shared/parcel-enums`) devolve todas
 * as listas, então não há ganho em separar por campo.
 *
 * **É o que torna `typeId` editável.** Enquanto este endpoint não existia, o campo
 * Tipo era exibido como leitura: fabricar as opções no frontend significaria
 * adivinhar ids, e nem o rótulo desambiguava — `Registration` (1) e `Enrollment`
 * (2) compartilham o texto "Inscrição" desde o legado. O backend agora devolve as
 * duas chaves, e ambas são valores válidos de `typeId`; o rótulo repetido é
 * esperado, não defeito.
 *
 * `paymentMethods` aqui é a lista **completa** do enum, usada para rotular; o
 * dropdown de escrita continua sendo o do contrato, que é restrito ao que o plano
 * aceita.
 *
 * @param enabled - `false` desabilita a query, para telas sem parcela em foco.
 * @returns Listas normalizadas e estados de carga.
 *
 * @example
 * ```tsx
 * const { types, isLoading } = useParcelEnums();
 * ```
 */
export function useParcelEnums(enabled = true): ParcelEnums {
  const { data, isLoading, isError } = useGetApiV1IndividualpaymentSharedParcelEnums(undefined, {
    query: { enabled, staleTime: PARCEL_ENUMS_STALE_TIME },
  });

  return {
    types: mapDropdownOptions(data?.types ?? undefined),
    statuses: mapDropdownOptions(data?.statuses ?? undefined),
    paymentMethods: mapDropdownOptions(data?.paymentMethods ?? undefined),
    isLoading: enabled && isLoading,
    isError,
  } as const;
}
